/**
 * VoteDeadline — countdown timer for voting deadline.
 * Renders nothing when no deadline is set.
 */
import React, { useState, useEffect } from 'react';

interface VoteDeadlineProps {
  deadline: string;
  isResolved: boolean;
  accentColor?: string;
}

function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return 'Voting closed';
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h remaining`;
  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  if (minutes > 0) return `${minutes}m remaining`;
  return 'Less than 1m remaining';
}

export const VoteDeadline: React.FC<VoteDeadlineProps> = ({
  deadline,
  isResolved,
  accentColor,
}) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const deadlineMs = new Date(deadline).getTime();
  const remaining = deadlineMs - now;
  const isPast = remaining <= 0;
  const isUrgent = remaining > 0 && remaining < 24 * 60 * 60 * 1000;
  const isCritical = remaining > 0 && remaining < 60 * 60 * 1000;

  if (isResolved) {
    return (
      <span style={{
        fontSize: 11,
        color: '#6B7280',
        padding: '2px 8px',
        borderRadius: 4,
        background: '#F3F4F6',
      }}>
        Voting complete
      </span>
    );
  }

  const color = isPast
    ? '#6B7280'
    : isCritical
      ? '#EF4444'
      : isUrgent
        ? '#F59E0B'
        : accentColor || '#6B7280';

  return (
    <span style={{
      fontSize: 11,
      color,
      fontWeight: isCritical ? 600 : 400,
      padding: '2px 8px',
      borderRadius: 4,
      background: isPast ? '#F3F4F6' : isCritical ? '#FEF2F2' : isUrgent ? '#FFFBEB' : '#F9FAFB',
    }}>
      {isPast ? 'Voting closed' : formatTimeRemaining(remaining)}
    </span>
  );
};

VoteDeadline.displayName = 'VoteDeadline';
